/* eslint-disable import/no-cycle */
/* eslint-disable dot-notation */
import { useStaticQuery, graphql } from 'gatsby'
import { useState, useEffect } from 'react'
import tw, { css } from 'twin.macro'
import { Global } from '@emotion/react'
import { localizedPath, useLocale } from '@plugins/bw-i18next'
import { documentToHtmlString } from '@contentful/rich-text-html-renderer'
// eslint-disable-next-line import/no-cycle
import RichText from '@/components/ui/rich-text'
import Spinner from '@/icons/bwi/spinner.svg'
import { isPrint } from '@/utils/print'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import { useScript } from 'usehooks-ts'
import { useLocation } from '@reach/router'

export const fragment = graphql`
  fragment HubSpotForm on ContentfulComponentHubSpotForm {
    __typename
    id
    contentful_id
    node_locale
    headline
    subtext {
      raw
      references {
        ...Link
      }
    }
    formId
    submitText
    afterSubmitText
    afterSubmitLink {
      url
    }
    translations {
      key
      phrase
      phraseRichText {
        raw
        references {
          __typename
          contentful_id
          url
        }
      }
    }
    plausibleEventName
  }
`

const rawRichTextToDocument = (raw?: string) => {
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

type HubspotFormProps = Omit<Queries.HubSpotFormFragment, '__typename' | 'contentful_id' | 'node_locale'>

export default function HubspotForm({
  id,
  formId,
  headline,
  subtext,
  submitText,
  afterSubmitText,
  afterSubmitLink,
  translations,
  plausibleEventName,
}: HubspotFormProps): JSX.Element {
  const [loaded, setLoaded] = useState(false)
  const [loadingError, setLoadingError] = useState(false)
  const { currentLocale } = useLocale()
  const targetId = `hsf-${id}`
  const { pathname } = useLocation()
  const defaultErrorMessage = useStaticQuery(graphql`
    query ContentfulComponentRichText {
      contentfulComponentRichText(contentful_id: { eq: "2AI0UGPhYPCrxtPyVYEUQw" }) {
        body {
          raw
          references {
            ...Link
          }
        }
      }
    }
  `)

  if (isPrint()) return null

  const getOptions = (body: { raw?: string; references: any }) => ({
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: any) =>
        `<p>${documentToHtmlString(node, getOptions({ ...node, references: body.references }) as any).replaceAll('\n', '<br />')}</p>`,
      [INLINES.ENTRY_HYPERLINK]: (node: { data: { target: { sys: { id: any } } }; content: { value: any }[] }) => {
        const ref = body.references?.find((r) => r.contentful_id === node?.data?.target?.sys?.id) || node?.data?.target
        if (!ref) return null
        return `<a href="${ref.url}" target="_blank">${node.content[0].value}</a>`
      },
    },
    renderText: (text: string) =>
      text
        .split('\n')
        .reduce(
          (children: any, textSegment: any, index: number) => [
            ...children,
            index > 0 && <br key={index} />,
            textSegment,
          ],
          []
        ),
  })

  const scriptStatus = useScript(`//js.hsforms.net/forms/embed/v2.js`, {
    removeOnUnmount: false,
  })

  // if we're a MIT form page in the event of an error we can direct to the contact form
  const checkMITFormPage = () => pathname.includes('/go/start')

  useEffect(() => {
    if (scriptStatus === 'ready') {
      setLoadingError(false)
      window.hbspt.forms.create({
        region: 'na1',
        portalId: '22371289',
        formId: `${formId}`,
        pageId: id,
        target: `#${targetId}`,
        locale: currentLocale,
        cssClass: plausibleEventName,
        ...(afterSubmitText && { inlineMessage: afterSubmitText }),
        ...(afterSubmitLink?.url &&
          !afterSubmitText && { redirectUrl: localizedPath(afterSubmitLink.url, currentLocale) }),
        translations: {
          [currentLocale]: {
            submitText,
            fieldLabels:
              translations
                ?.filter((t) => !t.key.startsWith('#') && !t.key.startsWith('.'))
                .reduce((acc, curr) => {
                  if (curr.phrase?.trim()?.length > 0) {
                    acc[curr.key] = curr.phrase
                  } else if (curr.phraseRichText?.raw) {
                    const document = rawRichTextToDocument(curr.phraseRichText.raw)
                    if (document) {
                      acc[curr.key] = documentToHtmlString(document, getOptions(curr.phraseRichText))
                    }
                  }
                  return acc
                }, {}) || {},
          },
        },
        onFormReady: () => {
          setLoaded(true)
          const translateElement = (el: HTMLElement, t: (typeof translations)[0]) => {
            if (el) {
              if (t.phrase?.trim()?.length > 0) {
                el.innerText = t.phrase
              } else if (t.phraseRichText?.raw) {
                const document = rawRichTextToDocument(t.phraseRichText.raw)
                if (document) {
                  el.innerHTML = documentToHtmlString(document, getOptions(t.phraseRichText))
                }
              }
            }
          }
          translations
            ?.filter((t) => t.key.startsWith('#'))
            .forEach((t) => {
              translateElement(document.getElementById(t.key.substring(1)), t)
            })

          translations
            ?.filter((t) => t.key.startsWith('.'))
            .forEach((t) => {
              translateElement(document.querySelector<HTMLElement>(t.key), t)
            })

          // Add tooltip to server select
          const serverSelectDescription = document.querySelector('.hs_trial_initiation_server_selected .hs-field-desc')
          if (serverSelectDescription) {
            serverSelectDescription.innerHTML = `<span class="bw-hs-toolip-content">${serverSelectDescription.innerHTML}</span>`
            serverSelectDescription.insertAdjacentHTML('afterbegin', '<span class="bw-mit-info-icon"></span>')
          }
        },
      })
    }
    if (scriptStatus === 'error') {
      setLoaded(true)
      setLoadingError(true)
    }
  }, [scriptStatus])

  return (
    <div className="bw-hubspot-form" tw="min-h-[10rem] print:hidden">
      <span className="bw-hubspot-form-copy">
        <h3 className="bw-hubspot-form-headline">{headline}</h3>
        {subtext && <RichText body={subtext as any} />}
      </span>
      {!loaded && (
        <div className="bw-hubspot-form-loader" tw="w-full flex justify-center p-8">
          <Spinner tw="animate-spin h-16 w-16 text-gray-400" />
        </div>
      )}
      {loadingError && (
        <div tw="border border-melonRed bg-gray-200 px-4 py-2 rounded-md">
          <RichText body={defaultErrorMessage.contentfulComponentRichText.body} />
        </div>
      )}

      <div className="bw-hs-form-container" id={targetId}>
        <Global
          styles={css`
            .hs-form {
              label {
                ${tw`text-indigoBlue`}
              }
              fieldset {
                ${tw`max-w-full!`}
              }
              br {
                ${tw`flex mb-2 content-[" "]`}
              }
              .actions {
                .hs-button {
                  ${tw`my-0`}
                }
              }
              .inputs-list {
                ${tw`list-none`}
                .hs-form-booleancheckbox-display {
                  ${tw`flex items-start gap-2`}
                  .hs-input {
                    ${tw`mt-1 lg:mt-2`}
                  }
                }
              }
              .hs-form-required {
                ${tw`ml-1 text-xs text-red-500 leading-[1.1]`}
              }
              .hs-form-field {
                ${tw`mb-4`}
              }
              .hs-input:not([type='radio']):not([type='checkbox']) {
                ${tw`bg-white p-3 placeholder:(text-gray-400) w-full rounded border  h-[50px] lg:h-[54px]  border-gray-300 hover:(border-gray-400) focus:(outline-none ring-2 ring-primaryBlue) transition-[border-color, box-shadow]`}
              }
              .hs-input.is-placeholder {
                ${tw`text-gray-600!`}
              }
              .hs-input[type='radio'] {
                ${tw`mr-2`}
              }
              select.hs-input {
                ${tw`w-full rounded border-r-8 border-transparent pl-3 pr-7 text-lg outline outline-[0px] appearance-none bg-no-repeat bg-[right_52%] bg-[length:40px_15px] leading-tight bg-white text-gray-700 bg-[url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iaXNvLTg4NTktMSI/Pgo8IS0tIEdlbmVyYXRvcjogQWRvYmUgSWxsdXN0cmF0b3IgMTkuMC4wLCBTVkcgRXhwb3J0IFBsdWctSW4gLiBTVkcgVmVyc2lvbjogNi4wMCBCdWlsZCAwKSAgLS0+CjwhLS0gTGljZW5zZTogQ0MwLiBNYWRlIGJ5IFNWRyBSZXBvOiBodHRwczovL3d3dy5zdmdyZXBvLmNvbS9zdmcvMTgzNTUyL2NoZXZyb24gLS0+CjxzdmcgdmVyc2lvbj0iMS4xIiBpZD0iTGF5ZXJfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgeD0iMHB4IiB5PSIwcHgiCgkgdmlld0JveD0iMCAwIDUxMiA1MTIiIHN0eWxlPSJlbmFibGUtYmFja2dyb3VuZDpuZXcgMCAwIDUxMiA1MTI7IiB4bWw6c3BhY2U9InByZXNlcnZlIj4KPHBhdGggc3R5bGU9ImZpbGw6IzZFNzg4MTsiIGQ9Ik01MDUuMTgzLDEyMy4xNzljLTkuMDg3LTkuMDg3LTIzLjgyNC05LjA4OS0zMi45MTIsMC4wMDJsLTIxNi4yNjYsMjE2LjI3TDM5LjcyOSwxMjMuMTc5CgljLTkuMDg3LTkuMDg3LTIzLjgyNC05LjA4OS0zMi45MTIsMC4wMDJjLTkuMDg5LDkuMDg5LTkuMDg5LDIzLjgyNCwwLDMyLjkxMkwyMzkuNTUsMzg4LjgyYzQuMzY0LDQuMzY0LDEwLjI4Myw2LjgxNiwxNi40NTUsNi44MTYKCWM2LjE3MiwwLDEyLjA5Mi0yLjQ1MywxNi40NTUtNi44MTdsMjMyLjcyMS0yMzIuNzI3QzUxNC4yNzIsMTQ3LjAwNCw1MTQuMjcyLDEzMi4yNjgsNTA1LjE4MywxMjMuMTc5eiIvPgo8L3N2Zz4K')]`}
                &:required:invalid {
                  ${tw`text-gray-400`}
                }
              }
              .hs-fieldtype-textarea {
                ${tw`w-full! min-h-[10rem]!`}
              }
              .hs-error-msgs {
                ${tw`list-none`}
                li {
                  ${tw`leading-[1.1]`}
                  label {
                    ${tw`ml-1 text-xs text-red-500`}
                  }
                }
              }
              .hs-button {
                ${tw`font-normal border-none text-base px-4 py-2 my-4 rounded-full text-center bg-primaryBlue text-white cursor-pointer hover:(bg-indigoBlue) transition transition-[background-color, color] ease-in-out duration-300 focus:(outline-none ring-2 ring-primaryBlue)`}
                &.large {
                  ${tw`px-6 py-3 text-lg`}
                }
              }
              .hs-richtext p {
                ${tw`text-sm leading-[1.25] text-gray-600`}
              }
              .legal-consent-container {
                p {
                  ${tw`text-xs leading-[1.1] text-gray-600`}
                }
                div.hs-fieldtype-booleancheckbox {
                  ul.inputs-list {
                    ${tw`my-6 list-none`}
                    li.hs-form-booleancheckbox {
                      label.hs-form-booleancheckbox-display {
                        ${tw`flex`}
                        input.hs-input {
                          ${tw`flex-none w-3 rounded`}
                        }
                        span {
                          ${tw`flex-1 leading-[1.1] text-gray-600 ml-2`}
                          p {
                            ${tw`leading-[1.1]`}
                          }
                        }
                      }
                    }
                  }
                }
              }
              // MIT form
              .hs_trial_initiation_server_selected {
                ${tw`relative mb-8`}

                .inputs-list {
                  ${tw`flex flex-wrap gap-4 mt-3`}

                  li {
                    ${tw`flex`}
                  }
                }
                .hs-form-radio-display {
                  ${tw`border-2 border-gray-300 rounded-lg border px-4 py-2 cursor-pointer transition-opacity`}
                }
                .hs-form-radio-display:hover {
                  ${tw`opacity-60`}
                }
                .hs-form-radio-display:has(.hs-input:checked) {
                  ${tw`border-primaryBlue text-primaryBlue opacity-100`}
                }
                .hs-input[type='radio'] {
                  ${tw`hidden`}
                }

                > label {
                  ${tw`align-middle mb-2 float-left`}
                }

                .input {
                  ${tw`clear-both`}
                }

                .hs-field-desc {
                  ${tw`relative inline-block! w-auto ml-2 float-left`}

                  p {
                    ${tw`text-sm mb-0`}
                  }
                }

                .bw-hs-toolip-content {
                  ${tw`absolute top-0 left-1/2 -translate-x-1/2 z-0 pointer-events-none`}
                  ${tw`bg-white w-[300px] p-4 rounded-lg border border-gray-300 shadow-md text-sm text-gray-600`}
                    ${tw`opacity-0 -translate-y-2 transition-[opacity, transform]`}

                    &:before {
                    ${tw`content-[''] absolute top-[-22px] left-1/2 -translate-x-1/2 box-border`}
                    ${tw`border-[11px] border-solid border-transparent border-b-gray-400`}
                  }
                  &:after {
                    ${tw`content-[''] absolute -top-5 left-1/2 -translate-x-1/2 box-border`}
                    ${tw`border-[10px] border-solid border-transparent border-b-white`}
                  }
                }

                .bw-mit-info-icon {
                  ${tw`relative text-gray-400 align-sub z-10`}

                  :after {
                    ${tw`inline-block w-4 h-4`}
                    content: url(data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgdmlld0JveD0iMCAwIDMyIDMyIj4KPHBhdGggZmlsbD0iI0FCQjRCRCIgZD0iTTE2IDI2LjM2Yy0wLjI2NSAwLTAuNTItMC4xMDUtMC43MDctMC4yOTNzLTAuMjkzLTAuNDQyLTAuMjkzLTAuNzA3di0xMmMwLTAuMjY1IDAuMTA1LTAuNTIgMC4yOTMtMC43MDdzMC40NDItMC4yOTMgMC43MDctMC4yOTNjMC4yNjUgMCAwLjUyIDAuMTA1IDAuNzA3IDAuMjkzczAuMjkzIDAuNDQyIDAuMjkzIDAuNzA3djEyYzAgMC4yNjUtMC4xMDUgMC41MTktMC4yOTMgMC43MDdzLTAuNDQyIDAuMjkzLTAuNzA3IDAuMjkzek0xNiA4Ljg3OGMwLjY1NSAwIDEuMTg2LTAuNTMxIDEuMTg2LTEuMTg2cy0wLjUzMS0xLjE4Ni0xLjE4Ni0xLjE4NmMtMC42NTUgMC0xLjE4NiAwLjUzMS0xLjE4NiAxLjE4NnMwLjUzMSAxLjE4NiAxLjE4NiAxLjE4NnpNMTYgMzJjLTMuMTY1IDAtNi4yNTgtMC45MzgtOC44ODktMi42OTZzLTQuNjgyLTQuMjU3LTUuODkzLTcuMTgxYy0xLjIxMS0yLjkyNC0xLjUyOC02LjE0MS0wLjkxLTkuMjQ0czIuMTQxLTUuOTU1IDQuMzc5LTguMTkyYzIuMjM4LTIuMjM4IDUuMDg5LTMuNzYxIDguMTkyLTQuMzc5czYuMzIxLTAuMyA5LjI0NCAwLjkxYzIuOTI0IDEuMjExIDUuNDIyIDMuMjYyIDcuMTgxIDUuODkzczIuNjk2IDUuNzI1IDIuNjk2IDguODg5Yy0wLjAwNSA0LjI0Mi0xLjY5MiA4LjMwOS00LjY5MiAxMS4zMDhzLTcuMDY2IDQuNjg3LTExLjMwOCA0LjY5MnpNMTYgMmMtMi43NjkgMC01LjQ3NiAwLjgyMS03Ljc3OCAyLjM1OXMtNC4wOTcgMy43MjUtNS4xNTYgNi4yODNjLTEuMDYwIDIuNTU4LTEuMzM3IDUuMzczLTAuNzk3IDguMDg5czEuODc0IDUuMjEgMy44MzIgNy4xNjhjMS45NTggMS45NTggNC40NTIgMy4yOTEgNy4xNjggMy44MzFzNS41MzEgMC4yNjMgOC4wODktMC43OTdjMi41NTgtMS4wNjAgNC43NDUtMi44NTQgNi4yODMtNS4xNTZzMi4zNTktNS4wMDkgMi4zNTktNy43NzhjLTAuMDA0LTMuNzEyLTEuNDgxLTcuMjctNC4xMDUtOS44OTVzLTYuMTgzLTQuMTAxLTkuODk1LTQuMTA1djB6Ij48L3BhdGg+Cjwvc3ZnPg==);
                  }
                }

                .bw-mit-info-icon:hover + .bw-hs-toolip-content,
                .bw-hs-toolip-content:hover {
                  ${tw`opacity-100 translate-y-9 pointer-events-auto`}
                }
              }
            }
          `}
        />
      </div>
    </div>
  )
}
